var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2024 Sequent Tech <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useGetOne, useNotify, useRecordContext, useRefresh } from "react-admin";
import { EPublishType } from "./EPublishType";
import { PUBLISH_BALLOT } from "@/queries/PublishBallot";
import { PublishStatus, MAP_ELECTION_EVENT_STATUS_PUBLISH, nextStatus, } from "./EPublishStatus";
import { GENERATE_BALLOT_PUBLICATION } from "@/queries/GenerateBallotPublication";
import { GET_BALLOT_PUBLICATION_CHANGE } from "@/queries/GetBallotPublicationChanges";
import { PublishList } from "./PublishList";
import { PublishGenerate } from "./PublishGenerate";
import { UPDATE_EVENT_VOTING_STATUS } from "@/queries/UpdateEventVotingStatus";
import { UPDATE_ELECTION_VOTING_STATUS } from "@/queries/UpdateElectionVotingStatus";
import { IPermissions } from "@/types/keycloak";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { SettingsContext } from "@/providers/SettingsContextProvider";
import { convertToNumber } from "@/lib/helpers";
import { EditPreview } from "./EditPreview";
import FormDialog from "@/components/FormDialog";
import { EPublishActions } from "@/types/publishActions";
var ViewMode;
(function (ViewMode) {
    ViewMode[ViewMode["Edit"] = 0] = "Edit";
    ViewMode[ViewMode["View"] = 1] = "View";
    ViewMode[ViewMode["List"] = 2] = "List";
})(ViewMode || (ViewMode = {}));
var PublishMemo = React.memo(function (_a) {
    var _b;
    var electionEventId = _a.electionEventId, electionId = _a.electionId, type = _a.type, showList = _a.showList;
    var MAX_DIFF_LINES = (_b = convertToNumber(process.env.MAX_DIFF_LINES)) !== null && _b !== void 0 ? _b : 500;
    var notify = useNotify();
    var t = useTranslation().t;
    var tenantId = useTenantStore()[0];
    var _c = useState(ViewMode.List), viewMode = _c[0], setViewMode = _c[1];
    var _d = useState(false), changingStatus = _d[0], setChangingStatus = _d[1];
    var _e = useState(PublishStatus.Void), publishStatus = _e[0], setPublishStatus = _e[1];
    var _f = React.useState(false), open = _f[0], setOpen = _f[1];
    var _g = useState(null), ballotPublicationId = _g[0], setBallotPublicationId = _g[1];
    var globalSettings = useContext(SettingsContext).globalSettings;
    var authContext = useContext(AuthContext);
    var isGoldUser = authContext.isGoldUser;
    var canWrite = authContext.isAuthorized(true, tenantId, IPermissions.PUBLISH_WRITE);
    var canRead = authContext.isAuthorized(true, tenantId, IPermissions.PUBLISH_READ);
    var record = useRecordContext();
    // Used to show the election status
    var _h = useState(null), electionStatus = _h[0], setElectionStatus = _h[1];
    var _j = useState(null), electionPresentation = _j[0], setElectionPresentation = _j[1];
    var refresh = useRefresh();
    var _k = useState(null), generateData = _k[0], setGenerateData = _k[1];
    var publishBallot = useMutation(PUBLISH_BALLOT)[0];
    var getBallotPublicationChanges = useMutation(GET_BALLOT_PUBLICATION_CHANGE)[0];
    var generateBallotPublication = useMutation(GENERATE_BALLOT_PUBLICATION)[0];
    var _l = useMutation(UPDATE_EVENT_VOTING_STATUS), updateStatusEvent = _l[0], updateStatusEventError = _l[1].error;
    var updateStatusElection = useMutation(UPDATE_ELECTION_VOTING_STATUS)[0];
    var _m = useGetOne("sequent_backend_ballot_publication", {
        id: ballotPublicationId,
    }, {
        enabled: !!ballotPublicationId,
    }), ballotPublication = _m.data, refetch = _m.refetch;
    var onPublish = function () { return __awaiter(void 0, void 0, void 0, function () {
        var data, e_1;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 4, , 5]);
                    if (!!ballotPublicationId) return [3 /*break*/, 2];
                    return [4 /*yield*/, onGenerate()];
                case 1:
                    _c.sent();
                    return [2 /*return*/];
                case 2:
                    handleSetPublishStatus(PublishStatus.PublishedLoading);
                    return [4 /*yield*/, publishBallot({
                            variables: {
                                electionEventId: electionEventId,
                                ballotPublicationId: ballotPublicationId,
                            },
                        })];
                case 3:
                    data = (_c.sent()).data;
                    if ((_a = data === null || data === void 0 ? void 0 : data.publish_ballot) === null || _a === void 0 ? void 0 : _a.ballot_publication_id) {
                        setBallotPublicationId((_b = data === null || data === void 0 ? void 0 : data.publish_ballot) === null || _b === void 0 ? void 0 : _b.ballot_publication_id);
                    }
                    refetch();
                    setViewMode(ViewMode.List);
                    notify(t("publish.notifications.published"), {
                        type: "success",
                    });
                    handleSetPublishStatus(PublishStatus.Void);
                    return [3 /*break*/, 5];
                case 4:
                    e_1 = _c.sent();
                    notify(t("publish.dialog.error_publish"), {
                        type: "error",
                    });
                    handleSetPublishStatus(PublishStatus.Void);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var kioskModeEnabled = function () {
        var _a, _b;
        return (_b = (_a = record === null || record === void 0 ? void 0 : record.voting_channels) === null || _a === void 0 ? void 0 : _a.kiosk) !== null && _b !== void 0 ? _b : false;
    };
    var onGenerate = function () { return __awaiter(void 0, void 0, void 0, function () {
        var data, e_2;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 2, , 3]);
                    setViewMode(ViewMode.Edit);
                    handleSetPublishStatus(PublishStatus.GeneratedLoading);
                    return [4 /*yield*/, generateBallotPublication({
                            variables: {
                                electionId: electionId,
                                electionEventId: electionEventId,
                            },
                        })];
                case 1:
                    data = (_c.sent()).data;
                    handleSetPublishStatus(PublishStatus.GeneratedLoading);
                    if ((_a = data === null || data === void 0 ? void 0 : data.generate_ballot_publication) === null || _a === void 0 ? void 0 : _a.ballot_publication_id) {
                        setBallotPublicationId((_b = data === null || data === void 0 ? void 0 : data.generate_ballot_publication) === null || _b === void 0 ? void 0 : _b.ballot_publication_id);
                    }
                    else {
                        throw "Publication Generation Error";
                    }
                    return [3 /*break*/, 3];
                case 2:
                    e_2 = _c.sent();
                    notify(t("publish.dialog.error"), {
                        type: "error",
                    });
                    handleSetPublishStatus(PublishStatus.Void);
                    setViewMode(ViewMode.List);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var onChangeStatus = function (electionEventStatus, votingChannel) {
        var publishStatus = MAP_ELECTION_EVENT_STATUS_PUBLISH[electionEventStatus];
        var newStatus = nextStatus(publishStatus);
        handleSetPublishStatus(newStatus);
        if (type === EPublishType.Election) {
            onChangeElectionStatus(electionEventStatus, votingChannel);
        }
        else if (type === EPublishType.Event) {
            onChangeElectionEventStatus(electionEventStatus, votingChannel);
        }
    };
    var onChangeElectionStatus = function (votingStatus, votingChannel) { return __awaiter(void 0, void 0, void 0, function () {
        var e_3;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    setChangingStatus(true);
                    return [4 /*yield*/, updateStatusElection({
                            variables: {
                                votingStatus: votingStatus,
                                electionId: electionId,
                                electionEventId: electionEventId,
                                votingChannel: votingChannel,
                            },
                        })
                        // TODO: Right now if we are opening or pausing for online, we
                        // also do it for kiosk if kiosk mode is enabled. In the future,
                        // we should be able to do this individually in the UI for each
                        // channel separatedly.
                    ];
                case 1:
                    _a.sent();
                    // TODO: Right now if we are opening or pausing for online, we
                    // also do it for kiosk if kiosk mode is enabled. In the future,
                    // we should be able to do this individually in the UI for each
                    // channel separatedly.
                    handleSetPublishStatus(MAP_ELECTION_EVENT_STATUS_PUBLISH[votingStatus]);
                    setChangingStatus(false);
                    refresh();
                    notify(t("publish.notifications.change_status"), {
                        type: "success",
                    });
                    return [3 /*break*/, 3];
                case 2:
                    e_3 = _a.sent();
                    setChangingStatus(false);
                    notify(t("publish.dialog.error_status"), {
                        type: "error",
                    });
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var onChangeElectionEventStatus = function (electionEventStatus, votingChannel) { return __awaiter(void 0, void 0, void 0, function () {
        var e_4;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    setChangingStatus(true);
                    return [4 /*yield*/, updateStatusEvent({
                            variables: {
                                electionEventId: electionEventId,
                                votingStatus: electionEventStatus,
                                votingChannel: votingChannel,
                            },
                        })];
                case 1:
                    _a.sent();
                    handleSetPublishStatus(MAP_ELECTION_EVENT_STATUS_PUBLISH[electionEventStatus]);
                    setChangingStatus(false);
                    refresh();
                    notify(t("publish.notifications.change_status"), {
                        type: "success",
                    });
                    return [3 /*break*/, 3];
                case 2:
                    e_4 = _a.sent();
                    setChangingStatus(false);
                    notify(t("publish.dialog.error_status"), {
                        type: "error",
                    });
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var fetchAllPublishChanges = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var data, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, getBallotPublicationChanges({
                            variables: {
                                electionEventId: electionEventId,
                                ballotPublicationId: ballotPublicationId,
                            },
                        })];
                case 1:
                    data = (_a.sent()).data.get_ballot_publication_changes;
                    setGenerateData(data);
                    return [3 /*break*/, 3];
                case 2:
                    error_1 = _a.sent();
                    setViewMode(ViewMode.List);
                    setGenerateData(null);
                    handleSetPublishStatus(PublishStatus.Void);
                    notify(t("publish.dialog.error"), {
                        type: "error",
                    });
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [ballotPublicationId, electionEventId, getBallotPublicationChanges]);
    var getPublishChanges = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var data, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, getBallotPublicationChanges({
                            variables: {
                                electionEventId: electionEventId,
                                ballotPublicationId: ballotPublicationId,
                                limit: MAX_DIFF_LINES / 10,
                            },
                        })];
                case 1:
                    data = (_a.sent()).data.get_ballot_publication_changes;
                    setGenerateData(data);
                    return [3 /*break*/, 3];
                case 2:
                    error_2 = _a.sent();
                    setViewMode(ViewMode.List);
                    setGenerateData(null);
                    handleSetPublishStatus(PublishStatus.Void);
                    notify(t("publish.dialog.error"), {
                        type: "error",
                    });
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [ballotPublicationId, electionEventId, getBallotPublicationChanges]);
    var handleSetPublishStatus = useCallback(function (status) {
        if (publishStatus !== PublishStatus.Stopped) {
            setPublishStatus(status);
        }
    }, [publishStatus]);
    var onPreview = function (id) {
        setBallotPublicationId(id);
        setOpen(true);
    };
    var handleCloseEditDrawer = function () {
        setOpen(false);
    };
    /**
     * Checks for any pending actions after the component mounts.
     * If a pending action is found, it executes the action and removes the flag.
     */
    useEffect(function () {
        var executePendingActions = function () { return __awaiter(void 0, void 0, void 0, function () {
            var isGold, pendingPublish;
            return __generator(this, function (_a) {
                isGold = isGoldUser();
                if (isGold) {
                    pendingPublish = sessionStorage.getItem(EPublishActions.PENDING_PUBLISH_ACTION);
                    if (pendingPublish) {
                        onGenerate();
                    }
                }
                return [2 /*return*/];
            });
        }); };
        var cleanup = function () {
            sessionStorage.removeItem(EPublishActions.PENDING_PUBLISH_ACTION);
        };
        if (electionEventId || electionId) {
            executePendingActions();
            cleanup();
        }
    }, [onChangeStatus, onGenerate]);
    useEffect(function () {
        if (showList) {
            setViewMode(ViewMode.List);
            setBallotPublicationId(null);
        }
    }, [showList]);
    useEffect(function () {
        if (electionEventId && ballotPublicationId && (ballotPublication === null || ballotPublication === void 0 ? void 0 : ballotPublication.is_generated)) {
            getPublishChanges();
        }
    }, [
        ballotPublicationId,
        ballotPublication === null || ballotPublication === void 0 ? void 0 : ballotPublication.is_generated,
        electionEventId,
        getPublishChanges,
    ]);
    // Used in order to make sure new generated publications are viewed when task completes
    useEffect(function () {
        if (ballotPublication && ballotPublication.is_generated === false) {
            var intervalId_1 = setInterval(function () {
                refetch();
            }, globalSettings.QUERY_POLL_INTERVAL_MS);
            return function () { return clearInterval(intervalId_1); };
        }
    }, [ballotPublication, refetch]);
    useEffect(function () {
        if (ballotPublicationId) {
            refetch();
        }
    }, [refetch, ballotPublicationId]);
    useEffect(function () {
        if (generateData) {
            handleSetPublishStatus(PublishStatus.Generated);
            if (!viewMode) {
                notify(t("publish.notifications.generated"), {
                    type: "success",
                });
            }
        }
    }, [t, notify, viewMode, handleSetPublishStatus, generateData]);
    useEffect(function () {
        var status = record === null || record === void 0 ? void 0 : record.status;
        handleSetPublishStatus((status === null || status === void 0 ? void 0 : status.voting_status)
            ? MAP_ELECTION_EVENT_STATUS_PUBLISH === null || MAP_ELECTION_EVENT_STATUS_PUBLISH === void 0 ? void 0 : MAP_ELECTION_EVENT_STATUS_PUBLISH[status === null || status === void 0 ? void 0 : status.voting_status]
            : PublishStatus.Void);
    }, [updateStatusEventError, handleSetPublishStatus, record]);
    useEffect(function () {
        var _a, _b;
        var status = (_a = record === null || record === void 0 ? void 0 : record.status) !== null && _a !== void 0 ? _a : null;
        var presentation = (_b = record === null || record === void 0 ? void 0 : record.presentation) !== null && _b !== void 0 ? _b : null;
        setElectionStatus(status);
        setElectionPresentation(presentation);
    }, [record]);
    return (_jsxs(Box, __assign({ sx: { flexGrow: 2, flexShrink: 0 } }, { children: [viewMode === ViewMode.List && (_jsx(PublishList, { status: publishStatus, electionStatus: electionStatus, electionPresentation: electionPresentation, canRead: canRead, publishType: type, canWrite: canWrite, kioskModeEnabled: kioskModeEnabled(), changingStatus: changingStatus, electionId: electionId, onGenerate: onGenerate, onChangeStatus: onChangeStatus, electionEventId: electionEventId, setBallotPublicationId: function (id) {
                    setViewMode(ViewMode.View);
                    setBallotPublicationId(id);
                }, onPreview: onPreview })), (viewMode === ViewMode.Edit || viewMode === ViewMode.View) && (_jsx(PublishGenerate, { ballotPublicationId: ballotPublicationId, status: publishStatus, changingStatus: changingStatus, readOnly: viewMode === ViewMode.View, data: generateData, publishType: type, onPublish: onPublish, electionId: electionId, onGenerate: onGenerate, onBack: function () {
                    refetch();
                    setViewMode(ViewMode.List);
                    handleSetPublishStatus(PublishStatus.Generated);
                    setGenerateData(null);
                    setBallotPublicationId(null);
                }, electionEventId: electionEventId, fetchAllPublishChanges: fetchAllPublishChanges, onPreview: onPreview })), _jsx(FormDialog, __assign({ open: open, onClose: handleCloseEditDrawer, title: t("publish.dialog.title") }, { children: _jsx(EditPreview, { publicationId: ballotPublicationId, electionEventId: electionEventId, close: handleCloseEditDrawer, ballotData: generateData }) }))] })));
});
PublishMemo.displayName = "Publish";
export var Publish = PublishMemo;
